// Copyright (c) 2020 WiseTime. All rights reserved.

import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "app/store"
import { DialogStatus } from "features/shared/Dialog"
import { SearchRecentManualTimeCommentsResponse, } from "generated/wisetime/user/preferences/v4/preferences_pb"
import { ManualTimeBlockListItemInfo } from "./manualTimeBlocks"
import { ActivityTypeOption, defaultActivityOption } from "./activities"
import * as Actions from "./actions"

export type ManualTimeState = {
  // Result manual time comments found by (searchTerm)
  cachedManualTimeComments: string[],

  dialogStatus: DialogStatus,

  // Remember user inputs in dialog
  inputActivity: ActivityTypeOption,
  inputDescription: string,
  inputTimeBlocks: ManualTimeBlockListItemInfo[],
}

export const initialState: ManualTimeState = {
  cachedManualTimeComments: [],
  dialogStatus: "CLOSED",
  inputActivity: defaultActivityOption(),
  inputDescription: "",
  inputTimeBlocks: [],
}

export const slice = createSlice({
  name: "manualTime",
  initialState,
  reducers: {
    clearCachedManualTimeComments: (state): void => {
      state.cachedManualTimeComments = []
    },
    setCachedManualTimeComments: (state, action: PayloadAction<SearchRecentManualTimeCommentsResponse.AsObject>): void => {
      state.cachedManualTimeComments = action.payload.manualTimeCommentsList
    },
    setDialogStatus: (state, action: PayloadAction<DialogStatus>): void => {
      state.dialogStatus = action.payload
    },
    setInputActivity: (state, action: PayloadAction<ActivityTypeOption>): void => {
      state.inputActivity = action.payload
    },
    setInputDescription: (state, action: PayloadAction<string>): void => {
      state.inputDescription = action.payload
    },
    setInputTimeBlocks: (state, action: PayloadAction<ManualTimeBlockListItemInfo[]>): void => {
      state.inputTimeBlocks = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(Actions.addManualTime, (state) => {
        state.dialogStatus = "IN_PROGRESS"
      })
      .addCase(Actions.addManualTimeFulfilled, (state) => {
        state.dialogStatus = "CLOSED"
        state.inputDescription = ""
        state.inputTimeBlocks = []
      })
  }
})

export const {
  clearCachedManualTimeComments,
  setCachedManualTimeComments,
  setDialogStatus,
  setInputActivity,
  setInputDescription,
  setInputTimeBlocks,
} = slice.actions

export const selectCachedManualTimeComments = (state: RootState): string[] => state.manualTime.cachedManualTimeComments
export const selectDialogStatus = (state: RootState): DialogStatus => state.manualTime.dialogStatus
export const selectInputActivity = (state: RootState): ActivityTypeOption => state.manualTime.inputActivity
export const selectInputDescription = (state: RootState): string => state.manualTime.inputDescription
export const selectInputTimeBlocks = (state: RootState): ManualTimeBlockListItemInfo[] => state.manualTime.inputTimeBlocks

export default slice.reducer
